/**
 * Main Application Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * The main application entry point with comprehensive features:
 * - Auth0 authentication integration and management
 * - Route configuration and navigation handling
 * - API initialization and token management
 * - Data preloading and caching strategies
 * - Profile creation and validation
 * - Audio context and player management
 * - Responsive layout and sidebar management
 * - Mandatory disclaimer and profile completion
 * - Global state management and error handling
 * 
 * Core Features:
 * - Auth0-based authentication with automatic login redirect
 * - API helper initialization with token management
 * - Automatic profile creation for new users
 * - JSON data preloading for performance optimization
 * - Audio player context for media content
 * - Daire AI sidebar integration
 * - Mandatory disclaimer modal for incomplete profiles
 * - Responsive navigation and header management
 * - Global error handling and recovery
 * 
 * Authentication Flow:
 * 1. Auth0 authentication check and redirect if needed
 * 2. API helper setup with token getter
 * 3. Profile existence check and creation if needed
 * 4. Data preloading based on route (home/splash pages)
 * 
 * Data Management:
 * - Strategic preloading of essential JSON files
 * - Cache management with expiration
 * - Refresh capability for all cached data
 * - Error handling for failed preloads
 * - Performance optimization with delays
 * 
 * UI Components:
 * - Header with refresh and loading states
 * - Main navigation and welcome header
 * - Footer with responsive hiding
 * - Daire sidebar for AI interactions
 * - Audio modal and mini player
 * - Mandatory disclaimer modal
 * 
 * @requires auth0-react Authentication provider
 * @requires react-router-dom Navigation and routing
 * @returns {JSX.Element} Main application with authentication and routing
 */
import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import apiHelper from './utils/apiHelper';
import errorLogger from './utils/errorLogger';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './css/ifia.css';
import './css/Spacers.css';

import { initializeAPI } from './utils/apiHelper';
import AuthWrapper from './components/Auth/AuthWrapper';
import { AuthContext } from './components/Auth/AuthContext';

// Components
import Header from './components/Common/Header';
import MainNav from './components/Common/MainNav';
import WelcomeHeader from './components/Common/WelcomeHeader';
import ScrollToTop from './components/Common/ScrollToTop';
import Footer from './components/Common/Footer';
import ErrorBoundary from './components/Common/ErrorBoundary';
import MaintenancePage from './components/Common/MaintenancePage';

import DaireSidebar from './components/Daire/DaireSidebar';
import MandatoryDisclaimer from './components/Common/MandatoryDisclaimer';
import { useProfileStatus } from './hooks/useProfileStatus';
import { AudioProvider } from './contexts/AudioContext';
import MiniAudioPlayer from './components/Common/MiniAudioPlayer';
import AudioModal from './components/Common/AudioModal';

// Pages
import Spotlight from './pages/Spotlight';
import NewsRoom from './pages/NewsRoom';
import PolicyRegulatory from './pages/PolicyRegulatory';
import Events from './pages/Events';
import EventPage from './pages/EventPage';

// Mobile Pages
import MobileSplash from './pages/mobile/MobileSplash';
import MobileNews from './pages/mobile/MobileNews';
import MobileNewsDetail from './pages/mobile/MobileNewsDetail';
import MobileEvents from './pages/mobile/MobileEvents';
import MobileEventDetail from './pages/mobile/MobileEventDetail';
import MobileMediaCentre from './pages/mobile/MobileMediaCentre';
import MobileETFBook from './pages/mobile/MobileETFBook';
import MobileDaireChat from './pages/mobile/MobileDaireChat';
import MobileRedirect from './components/Common/MobileRedirect';
import MobilePlaceholder from './components/mobile/MobilePlaceholder';
import MobileNotAvailable from './components/mobile/MobileNotAvailable';
import NotFound from './components/Common/NotFound';
import WorkingGroups from './pages/WorkingGroups';
import WorkingGroup from './pages/WorkingGroup';
import SteeringGroup from './pages/SteeringGroup';
import CapabilityInnovation from './pages/CapabilityInnovation';
import Capability from './pages/Capability';
import CouncilCorner from './pages/CouncilCorner';
import Representation from './pages/Representation';
import Search from './pages/Search';
import Splash from './pages/Splash';
import Daire from './pages/DairePage';
import Profile from './pages/Profile';
import WGProfile from './pages/WGProfile';
import AuthLogin from './pages/AuthLogin';
import ForgotPassword from './pages/ForgotPassword';
import Register from './pages/Register';
import { AuthContextProvider } from './components/Auth/AuthContext';
import ApiHealthGuard from './components/Auth/ApiHealthGuard';
import News from './pages/News';
import File from './pages/File';
import MediaCentre from './pages/MediaCentre';
import ETFBook from './pages/ETFBook';
import Disclaimer from './pages/Disclaimer';

const { FILE_NAMES } = require('./utils/constants');
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));


const preloadJSONFiles = async (files, bypassCache = false) => {
  for (const fileName of files) {
    try {
      await apiHelper.fetchJSONFileByNameAndContent(fileName, bypassCache);
    } catch (error) {
      console.error(`Error preloading ${fileName}:`, error);
    }
    await delay(1000);
  }
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const isRootOrSplash = location.pathname === '/' || location.pathname === '/splash';
  const isMobileRoute = location.pathname.startsWith('/mobile');
  const isDisclaimerPage = location.pathname.toLowerCase().startsWith('/disclaimer');
  const { user: [user], loading: [isLoading] } = useContext(AuthContext);
  const isAuthenticated = !!user;
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isDaireSidebarOpen, setIsDaireSidebarOpen] = useState(false);
  const [apiInitialized, setApiInitialized] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  
  // Profile completion and disclaimer status
  const { showWarning, loading: profileLoading } = useProfileStatus();
  const [disclaimerCompleted, setDisclaimerCompleted] = useState(false);

  // Reset disclaimer completion when profile changes
  useEffect(() => {
    if (!profileLoading && showWarning) {
      setDisclaimerCompleted(false); // Reset if profile now shows warning
    }
  }, [showWarning, profileLoading]);

  const filesToPreload = [
    FILE_NAMES.SETTINGS,
    FILE_NAMES.PAGECONTENT,
    FILE_NAMES.PAGES,
    FILE_NAMES.MONTHLY_NEWS,
    FILE_NAMES.FEATURED_NEWS,
    FILE_NAMES.ETF_BOOK,
    FILE_NAMES.ETF_BOOK_GRAPH,
    FILE_NAMES.REPOSITORY_ITEMS,
    FILE_NAMES.BENEFITS,
    FILE_NAMES.TIMELINE,
    FILE_NAMES.TICKER,
    FILE_NAMES.KEY_POLICY_MESSAGES,
    FILE_NAMES.CBI_QUARTERLY_UPDATES,
    FILE_NAMES.CONSULTATIONS,
    FILE_NAMES.NEWS,
    FILE_NAMES.FEATURED_AND_SUBMITTED_ARTICLES,
    FILE_NAMES.TRIAGE,
    FILE_NAMES.EXTERNAL_INFO_SOURCES_CATEGORIES,
    FILE_NAMES.EXTERNAL_INFO_SOURCES,
    FILE_NAMES.WG_STRUCTURE,
    FILE_NAMES.WG_PERIODIC_UPDATES,
    FILE_NAMES.WG_MEMBER_DETAILS,
    FILE_NAMES.WG_TOR,
    FILE_NAMES.CAPABILITIES,
    FILE_NAMES.WG_MINUTES,
    FILE_NAMES.WG_REFERENCE_DOCS,
    FILE_NAMES.DAIRE_SUGGESTIONS,
    FILE_NAMES.DAIRE_OPTIONS
  ];

  useEffect(() => {
    const sendPageView = async () => {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'user_profile_event',
          userProfile: {
            cohort: userProfile.memberCohort,
            yearsInIndustry: userProfile.NoOfYrsintheIndustry
          }
        });
      } catch (error) {
        console.error('GTM pageView error:', error);
      }
    };
  
    if (user?.profile?.email && userProfile) {
      sendPageView();
    }
  }, [location.pathname, userProfile]);

  // Handle API initialization and data preloading
 useEffect(() => {
  const initApp = async () => {
    try {
      if (isAuthenticated && !apiInitialized && user?.profile?.email && user?.session?.accessToken) {
        const token = user.session.accessToken;
        
        // Create a dynamic token getter that can refresh tokens
        const dynamicTokenGetter = async (options = {}) => {
          try {
            // If we have a current token and it's still valid, use it
            if (window._accessToken) {
              // Check if token is expired by trying to decode it
              const tokenParts = window._accessToken.split('.');
              if (tokenParts.length === 3) {
                try {
                  const payload = JSON.parse(atob(tokenParts[1]));
                  const currentTime = Math.floor(Date.now() / 1000);
                  // If token expires in more than 5 minutes, use it
                  if (payload.exp && payload.exp > currentTime + 300) {
                    return window._accessToken;
                  }
                } catch (e) {
                  // Token decode error, refresh token
                }
              }
            }
            
            // Token is expired or invalid, get a fresh one
            return new Promise((resolve, reject) => {
              const { webAuth } = require('./utils/userJourneyHelper');
              webAuth.checkSession({
                audience: options.audience || process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'read:current_user openid profile update:current_user_metadata'
              }, (err, authResult) => {
                if (err) {
                  // If refresh fails, try to use the existing token as fallback
                  if (window._accessToken) {
                    resolve(window._accessToken);
                  } else {
                    reject(new Error('Token refresh failed and no fallback token available'));
                  }
                  return;
                }
                
                if (authResult && authResult.accessToken) {
                  // Update the global token
                  window._accessToken = authResult.accessToken;
                  resolve(authResult.accessToken);
                } else {
                  reject(new Error('No access token in refresh response'));
                }
              });
            });
          } catch (error) {
            // Fallback to existing token if available
            if (window._accessToken) {
              return window._accessToken;
            }
            throw error;
          }
        };
        
        // Set up the API helper with the dynamic token getter
        apiHelper.setAuth0TokenGetter(dynamicTokenGetter);

        // Test token availability
        if (!token) {
          errorLogger.logAuthError('no-token', {
            context: 'initial_token_test',
            userEmail: user?.profile?.email
          });
          return;
        }

        await initializeAPI();

        // Wait a moment to ensure everything is set up and tokens are propagated
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Now check and create profile if needed
        try {
          const userProfile = await apiHelper.getMemberProfileByEmail(user.profile.email);
          setUserProfile(userProfile);
        } catch (error) {
          if (error.message.includes('404')) {
            try {
              const response = await fetch(`${process.env.REACT_APP_API_URL}graph/createProfile`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  email: user.profile.email,
                  name: user.profile.name || '',
                })
              });

              if (!response.ok) {
                throw new Error(`Failed to create profile: ${response.statusText}`);
              }

            } catch (createError) {
              console.error('Error creating profile:', createError);
              errorLogger.logApiError(
                'graph/createProfile',
                createError,
                { method: 'POST', body: { email: user.profile.email, name: user.profile.name } }
              );
            }
          } else {
            console.error('Error checking profile:', error);
            errorLogger.logApiError(
              'graph/getMemberProfileByEmail',
              error,
              { method: 'GET' }
            );
          }
        }

        // Log successful login activity
        try {
          await apiHelper.logUserLogin(user.profile.email);
        } catch (loginLogError) {
          // Don't disrupt the login flow if activity logging fails
          errorLogger.logActivityError(loginLogError, {
            userEmail: user.profile.email,
            activityType: 'LOGIN'
          });
        }

          // Mark API as initialized only after everything is done
          setApiInitialized(true);

          // Only preload data on home/splash page
          if (isRootOrSplash) {
            await preloadJSONFiles(filesToPreload);
          } 
        }
              } catch (error) {
          console.error('Error initializing app:', error);
          errorLogger.logError(error, {
            type: 'APP_INITIALIZATION_ERROR',
            isAuthenticated,
            apiInitialized
          }, user?.profile?.email);
        }
    };
    initApp();
  }, [isAuthenticated, apiInitialized, user]);

  // Auth0 redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const path = location.pathname.toLowerCase();

    const isOnAuthOrPublicPage = 
      path.includes('/auth/login') ||
      path.includes('/auth/register') ||
      path.includes('/auth/forgotpassword') ||
      path.includes('/disclaimer');

    if (!isOnAuthOrPublicPage) {
      return navigate('/Auth/Login');
    }
      
    }
  }, [isLoading, isAuthenticated, isMobileRoute]);

  // Page visit tracking
  useEffect(() => {
    // Only track page visits if user is authenticated and API is initialized
    if (isAuthenticated && apiInitialized && user?.profile?.email) {
      const logPageVisit = async () => {
        try {
          // Get page title from document or create a readable title from path
          let pageTitle = document.title;
          
          // If title is generic, create a better one from the path
          if (pageTitle === 'IFIA Member Portal' || !pageTitle) {
            const pathParts = location.pathname.split('/').filter(part => part);
            if (pathParts.length === 0) {
              pageTitle = 'Home';
            } else {
              pageTitle = pathParts.map(part => 
                part.charAt(0).toUpperCase() + part.slice(1).replace(/([A-Z])/g, ' $1')
              ).join(' > ');
            }
          }

          await apiHelper.logPageVisit(user.profile.email, location.pathname, pageTitle);
                  } catch (error) {
            // Don't disrupt navigation if tracking fails
            errorLogger.logActivityError(error, {
              userEmail: user.profile.email,
              activityType: 'PAGE_VISIT',
              pathname: location.pathname
            });
          }
      };

      // Small delay to ensure page has loaded before logging
      const timer = setTimeout(logPageVisit, 500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isAuthenticated, apiInitialized, user?.profile?.email]);

  const handleRefreshAll = async () => {
    setIsLoadingData(true);
    try {
      await preloadJSONFiles(filesToPreload, true);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Check for maintenance mode - this overrides everything else
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';
  
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  if (!isAuthenticated) {
    return (
      <>
      <ScrollToTop/>
      <div className={`main-content ${isDaireSidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="content-wrapper">
          <div className="pagecontainer">
            <Routes>
              <Route path="/Auth/Login" element={<AuthLogin />} />
              <Route path="/Auth/Register" element={<Register />} />
              <Route path="/Auth/ForgotPassword" element={<ForgotPassword />} />
              <Route path="/Disclaimer" element={<Disclaimer />} />
            </Routes>
          </div>
        </div>
      </div>
    </>
    )
  }

  const hideNav = new URLSearchParams(window.location.search).get('fullScreen') === 'true' || isMobileRoute;
  const hideFooter = new URLSearchParams(window.location.search).get('fullScreen') === 'true' || isMobileRoute;

  // Mobile redirect wrapper with authentication check
  const mobileRedirectWrapper = (
    <ApiHealthGuard maxFailures={5} resetWindow={60000}>
      <div className={`main-content ${isDaireSidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="content-wrapper">
          {!hideNav && (
            <>
              <Header onRefreshAll={handleRefreshAll} isLoadingData={isLoadingData} />
              
              {!isRootOrSplash && (
                <>
                  <MainNav />
                    {!isDisclaimerPage && (
                    <WelcomeHeader
                    isDaireSidebarOpen={isDaireSidebarOpen}
                    setIsDaireSidebarOpen={() => setIsDaireSidebarOpen(true)}
                  />
                    )}
                </>
              )}
            </>
          )}
          <div className="pagecontainer">
            <Routes>
            {/* Desktop Routes */}
            <Route path="/" element={<Splash />} />
            <Route path="/Splash" element={<Splash />} />
            <Route path="/Spotlight" element={<Spotlight />} />
            <Route path="/NewsRoom" element={<NewsRoom />} />
            <Route path="/News/:id" element={<News />} />
            <Route path="/News/:type/:id" element={<News />} />
            <Route path="/Events" element={<Events />} />
            <Route path="/Events/:id" element={<EventPage />} />
            <Route path="/MediaCentre" element={<MediaCentre />} />
            <Route path="/PolicyRegulatory" element={<PolicyRegulatory />} />
            <Route path="/WorkingGroups" element={<WorkingGroups />} />
            <Route path="/WorkingGroups/WorkingGroup/:id" element={<WorkingGroup />} />
            <Route path="/WorkingGroups/SteeringGroup/:id" element={<SteeringGroup />} />
            <Route path="/CapabilityInnovation" element={<CapabilityInnovation />} />
            <Route path="/CapabilityInnovation/Capability/:id" element={<Capability />} />
            <Route path="/CouncilCorner" element={<CouncilCorner />} />
            <Route path="/Representation" element={<Representation />} />
            <Route path="/Members/MemberProfile/:id" element={<WGProfile />} />
            <Route path="/Members/MemberProfile" element={<WGProfile />} />
            <Route path="/Search" element={<Search />} />
            <Route path="/Daire" element={<Daire />} />
            <Route path="/Profile" element={<Profile />} />
            <Route path="/file" element={<File />} />
            <Route path="/files/detail/:slug" element={<File />} />
            <Route path="/files/detail/news/:articleId/:filename" element={<File />} />
            <Route path="/MediaCentre" element={<MediaCentre />} />
            <Route path="/MediaCentre/series/:series" element={<MediaCentre />} />
            <Route path="/MediaCentre/video/:id" element={<MediaCentre />} />
            <Route path="/etfbook" element={<ETFBook />} />
            <Route path="/Disclaimer" element={<Disclaimer noAuth={false} />} />
            
            {/* Mobile Routes */}
            <Route path="/mobile" element={<MobileSplash />} />
            <Route path="/mobile/splash" element={<MobileSplash />} />
            <Route path="/mobile/news" element={<MobileNews />} />
            <Route path="/mobile/news/:id" element={<MobileNewsDetail />} />
            <Route path="/mobile/events" element={<MobileEvents />} />
            <Route path="/mobile/events/:id" element={<MobileEventDetail />} />
            <Route path="/mobile/media" element={<MobileMediaCentre />} />
            <Route path="/mobile/policy" element={<MobilePlaceholder title="Policy & Regulatory" icon="fas fa-gavel" />} />
            <Route path="/mobile/groups" element={<MobilePlaceholder title="Working Groups" icon="fas fa-users" />} />
            <Route path="/mobile/innovation" element={<MobilePlaceholder title="Innovation" icon="fas fa-lightbulb" />} />
            <Route path="/mobile/council" element={<MobilePlaceholder title="Council Corner" icon="fas fa-building" />} />
            <Route path="/mobile/etf" element={<MobileETFBook />} />
            <Route path="/mobile/profile" element={<MobilePlaceholder title="Profile" icon="fas fa-user" />} />
            <Route path="/mobile/daire" element={<MobileDaireChat />} />
            <Route path="/mobile/search" element={<MobilePlaceholder title="Search" icon="fas fa-search" />} />
            <Route path="/mobile/feedback" element={<MobilePlaceholder title="Feedback" icon="fas fa-envelope" />} />
            
            {/* Catch-all route for mobile only - show not available page */}
            <Route path="/mobile/*" element={<MobileNotAvailable />} />
            
            {/* Catch-all route for desktop - show 404 page */}
            <Route path="*" element={<NotFound />} />
             </Routes> 
        </div>
        {!hideFooter && <Footer />}
      </div>
      
      {/* Daire Sidebar */}
      {isDaireSidebarOpen && (
        <DaireSidebar 
          isOpen={isDaireSidebarOpen}
          onClose={() => setIsDaireSidebarOpen(false)}
        />
      )}
    </div>
    </ApiHealthGuard>
  );

  // Show loading for all users but allow mobile routes to pass through
  if (isLoading) {
    return (
      <AudioProvider>
        <ScrollToTop/>
        {/* Apply mobile redirect during loading for immediate redirect */}
        <MobileRedirect>
          {mobileRedirectWrapper}
        </MobileRedirect>
      </AudioProvider>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const handleDisclaimerComplete = () => {
    setDisclaimerCompleted(true);
  };

  // Show mandatory disclaimer if profile is incomplete and not yet completed
  const shouldShowDisclaimer = !profileLoading && showWarning && !disclaimerCompleted;
  
  return (
    <AudioProvider>
      <ScrollToTop/>
      
      {/* Mandatory Disclaimer Modal - blocks everything until completed */}
      <MandatoryDisclaimer 
        show={shouldShowDisclaimer}
        onComplete={handleDisclaimerComplete}
      />
      
      {/* Global Audio Components */}
      <AudioModal />
      <MiniAudioPlayer />
      
      
      
      {/* Apply mobile redirect after authentication is complete */}
      <MobileRedirect>
        {mobileRedirectWrapper}
      </MobileRedirect>
    </AudioProvider>
  );
}

function App() {
  return (
      <Router>
        <AuthContextProvider>
          <ErrorBoundary name="MainApp">
            <AuthWrapper>
              <AppContent />
            </AuthWrapper>
          </ErrorBoundary>
        </AuthContextProvider>
      </Router>
  );
}

export default App;
